import L, { LatLngBounds, LatLngExpression } from 'leaflet';
import { MapMarker, MapBounds, ClusterGroup } from '../../../types/map';
import { HotelData } from '../../../modules/HotelDetails/hotelSlice';
import { AttractionProduct } from '../../../modules/AttractionPage/AttractionTypes';
import { currencySignsObj } from '@skygroup/shared/utils/constants';
import { FlightBundle } from '../../../interfaces/flightInterface';
import { getTotalPaxFromMultiRoom } from '../../../utils/convertEurToUsd';
import { Hotel } from '../../../modules/PackageDetails/packageDataInterface';

// Utility constants for clustering
export const CLUSTER_DISTANCE_THRESHOLD = 0.1;
export const MIN_ZOOM_FOR_CLUSTERING = 12;
export const DEFAULT_POSITION = { lat: 41.0082, lng: 28.9784 };
export const DEFAULT_ZOOM = 10;
export const PREVIEW_DEFAULT_ZOOM = 5;
export const MAP_MAX_ZOOM = 22;
export const MAP_MAX_NATIVE_ZOOM = 18;

// tileLayerSetOpenstreetmap is in Local language on each country
// TODO: Update naming 
export const tileLayerSetOpenstreetmap = {
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
};

export const tileLayerSetCartodb = {
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://www.carto.com/">CARTO</a>',
  url: 'https://cartodb-basemaps-a.global.ssl.fastly.net/rastertiles/voyager/{z}/{x}/{y}{r}.png',
};

export const tileLayerSetCartocdn = {
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> ' +
    '&copy; <a href="https://carto.com/attributions">CARTO</a>',
  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  subdomains: ['a', 'b', 'c', 'd'],
  errorTileUrl:
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==',
  maxNativeZoom: MAP_MAX_NATIVE_ZOOM,
};

export const tileLayerCartoVoyager = {
  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  subdomains: ['a', 'b', 'c', 'd'],
  attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
  maxNativeZoom: MAP_MAX_NATIVE_ZOOM
};
export const tileLayerCartoPositronLight = {
  url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
  subdomains: ['a','b','c','d'],
  attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
  maxNativeZoom: MAP_MAX_NATIVE_ZOOM
};
export const tileLayerCartoPositronDark = {
  url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  subdomains: ['a','b','c','d'],
  attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
  maxNativeZoom: MAP_MAX_NATIVE_ZOOM
};



export enum MARKER_TYPE {
  HOTEL = 'hotel',
  DP = 'dp',
  ATTRACTION = 'attraction',
  EVENT = 'event',
}


export const colors = [
  'gray',
  'fe6c58',
  'fe9068',
  'feb478',
  'fed686',
  'fed686',
];
export const labels = [
  'No Stars',
  '1 Stars',
  '2 Stars',
  '3 Stars',
  '4 Stars',
  '5 Stars',
];

export function customMarkerIcon(color: string) {
  const svgTemplate = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" class="marker">
      <path fill-opacity=".25" d="M16 32s1.427-9.585 3.761-12.025c4.595-4.805 8.685-.99 8.685-.99s4.044 3.964-.526 8.743C25.514 30.245 16 32 16 32z"/>
      <path fill="#${color}" stroke="#fff" d="M15.938 32S6 17.938 6 11.938C6 .125 15.938 0 15.938 0S26 .125 26 11.875C26 18.062 15.938 32 15.938 32zM16 6a4 4 0 100 8 4 4 0 000-8z"/>
    </svg>`;

  return new L.DivIcon({
    className: 'test',
    html: svgTemplate,
    iconSize: [40, 40],
    iconAnchor: [12, 24],
    popupAnchor: [7, -16],
  });
}

/**
 * Calculate distance between two geographical positions
 */
export const calculateDistance = (
  pos1: LatLngExpression,
  pos2: LatLngExpression
): number => {
  const [lat1, lng1] = Array.isArray(pos1) ? pos1 : [pos1.lat, pos1.lng];
  const [lat2, lng2] = Array.isArray(pos2) ? pos2 : [pos2.lat, pos2.lng];

  return Math.sqrt(Math.pow(lat2 - lat1, 2) + Math.pow(lng2 - lng1, 2));
};

/**
 * Create clusters from hotels based on zoom level and proximity
 */
export const createClusters = (
  hotels: MapMarker[],
  zoom: number
): (MapMarker | ClusterGroup)[] => {
  if (zoom >= MIN_ZOOM_FOR_CLUSTERING) {
    return hotels; // No clustering at high zoom levels
  }

  const clusters: ClusterGroup[] = [];
  const usedHotels = new Set<string>();

  hotels.forEach((hotel) => {
    if (usedHotels.has(hotel.identifier)) return;

    const nearbyHotels = hotels.filter((otherHotel) => {
      if (
        usedHotels.has(otherHotel.identifier) ||
        otherHotel.identifier === hotel.identifier
      ) {
        return false;
      }
      return (
        calculateDistance(hotel.position, otherHotel.position) <
        CLUSTER_DISTANCE_THRESHOLD
      );
    });

    if (nearbyHotels.length > 0) {
      // Create cluster
      const allHotels = [hotel, ...nearbyHotels];
      const centerLat =
        allHotels.reduce((sum, h) => {
          const [lat] = Array.isArray(h.position)
            ? h.position
            : [h.position[0]];
          return sum + lat;
        }, 0) / allHotels.length;

      const centerLng =
        allHotels.reduce((sum, h) => {
          const [, lng] = Array.isArray(h.position)
            ? h.position
            : [0, h.position[1]];
          return sum + lng;
        }, 0) / allHotels.length;

      const clusterRadius = calculateClusterRadius(allHotels);

      clusters.push({
        id: `cluster-${hotel.identifier}`,
        position: [centerLat, centerLng],
        hotels: allHotels,
        count: allHotels.length,
        radius: clusterRadius,
      });

      // Mark hotels as used
      allHotels.forEach((h) => usedHotels.add(h.identifier));
    } else {
      // Single hotel
      usedHotels.add(hotel.identifier);
    }
  });

  // Add remaining single hotels
  const singleHotels = hotels.filter(
    (hotel) => !usedHotels.has(hotel.identifier)
  );

  return [...clusters, ...singleHotels];
};

/**
 * Check if a position is within given map bounds
 */
export const isInBounds = (
  position: LatLngExpression,
  bounds: MapBounds
): boolean => {
  const [lat, lng] = Array.isArray(position)
    ? position
    : [position.lat, position.lng];
  return (
    lat >= bounds.south &&
    lat <= bounds.north &&
    lng >= bounds.west &&
    lng <= bounds.east
  );
};

/**
 * Transform hotel data to include position coordinates
 */
export const transformHotelsToMapMarkers = (
  hotels: HotelData[]
): MapMarker[] | [] => {
  const hotelsToProcess = hotels.length > 0 ? hotels : [];

  const processedHotels = (hotelsToProcess as any[])
    .filter((hotel: any) => hotel.info?.latitude && hotel.info?.longitude)
    .map((hotel: any) => ({
      ...hotel,
      price: hotel.lowest_room_rate.total_price,
      currency: hotel.lowest_room_rate.currency,
      position: [hotel.info.latitude, hotel.info.longitude],
      markerType: MARKER_TYPE.HOTEL
    }));

  return processedHotels;
};

/**
 * Transform hotel data to include position coordinates
 */
// MapHelpers.ts (replace the old implementation)
export const transformDynamicPackagesToMapMarkers = (
  hotels: HotelData[] = [],
  cheapestFlightData: FlightBundle,
  localParams?: any
): MapMarker[] => {
  // Normalize incoming hotels array
  const hotelsToProcess = Array.isArray(hotels) ? hotels : [];

  // If caller provided local params, use them; otherwise read from URL
  const paramsSource = localParams ? localParams : Object.fromEntries(new URL(window.location.href).searchParams);

  // Helper: parse multi_room_guests from various possible shapes
  const parseMultiRoomGuests = (val: any): { adult: number; child: number; infant: number }[] => {
    const defaultRooms = [{ adult: 0, child: 0, infant: 0 }];

    if (val == null || val === '') return defaultRooms;

    // If it's already an array/object, return it (or wrap object into array)
    if (Array.isArray(val)) {
      // ensure items have numeric values
      return val.map((r) => ({
        adult: Number(r?.adult ?? r?.a ?? 0),
        child: Number(r?.child ?? r?.c ?? 0),
        infant: Number(r?.infant ?? r?.i ?? 0),
      }));
    }
    if (typeof val === 'object') {
      // single room object
      return [
        {
          adult: Number(val?.adult ?? val?.a ?? 0),
          child: Number(val?.child ?? val?.c ?? 0),
          infant: Number(val?.infant ?? val?.i ?? 0),
        },
      ];
    }

    // If it's a string - try JSON.parse first
    if (typeof val === 'string') {
      // Trim whitespace
      const s = val.trim();

      // Try direct JSON parse
      try {
        const parsed = JSON.parse(s);
        // if parsed is array/object, recursing into parseMultiRoomGuests will normalize it
        return parseMultiRoomGuests(parsed);
      } catch (err) {
        // try decodeURIComponent then parse (in case it's URL encoded)
        try {
          const decoded = decodeURIComponent(s);
          const parsed2 = JSON.parse(decoded);
          return parseMultiRoomGuests(parsed2);
        } catch (err2) {
          // fallback: try to extract numbers using regex for pattern like "[adult: 2,child: 0, infant: 0]"
          const adultMatch = s.match(/adult\s*[:=]\s*(\d+)/i);
          const childMatch = s.match(/child\s*[:=]\s*(\d+)/i);
          const infantMatch = s.match(/infant\s*[:=]\s*(\d+)/i);

          if (adultMatch || childMatch || infantMatch) {
            return [
              {
                adult: Number(adultMatch?.[1] ?? 0),
                child: Number(childMatch?.[1] ?? 0),
                infant: Number(infantMatch?.[1] ?? 0),
              },
            ];
          }

          // Last fallback: check for comma-separated numeric values like "2,0,0"
          const nums = s.split(',').map((x) => Number(x.trim())).filter((n) => !Number.isNaN(n));
          if (nums.length >= 1) {
            return [
              {
                adult: nums[0] ?? 0,
                child: nums[1] ?? 0,
                infant: nums[2] ?? 0,
              },
            ];
          }

          // Cannot parse -> default
          return defaultRooms;
        }
      }
    }

    // All other cases -> default
    return defaultRooms;
  };

  // Read multi_room_guests value from paramsSource safely
  const multiRoomGuestsRaw = paramsSource?.multi_room_guests ?? paramsSource?.multi_room_guests; // same name, defensive
  const parsedMultiRoomGuests = parseMultiRoomGuests(multiRoomGuestsRaw);

  // Read adult/child from params fallback (string -> number)
  const adultParam = Number(paramsSource?.adult ?? 0) || 0;
  const childParam = Number(paramsSource?.child ?? 0) || 0;
  const infantParam = Number(paramsSource?.infant ?? 0) || 0;

  // Build processed hotels
  const processedHotels: MapMarker[] = hotelsToProcess
    .filter((hotel: any) => hotel?.info?.latitude && hotel?.info?.longitude)
    .map((hotel: any) => {
      const totalPax = getTotalPaxFromMultiRoom({
        multi_room_guests: parsedMultiRoomGuests,
        adult: adultParam,
        child: childParam,
      });

      return {
        ...hotel,
        price: hotel?.lowest_room_rate?.total_price ?? hotel?.price ?? 0,
        currency: hotel?.lowest_room_rate?.currency ?? hotel?.currency ?? '',
        position: [hotel.info.latitude, hotel.info.longitude],
        markerType: MARKER_TYPE.DP,
        flightPriceInfo: {
          price: cheapestFlightData?.price?.grandTotal ?? cheapestFlightData?.price?.total ?? 0,
          currency: cheapestFlightData?.price?.currency_sign ?? '',
          totalPax,
        },
      } as MapMarker;
    });

  return processedHotels;
};

/**
 * Transform attractions data to include position coordinates
 */
export const transformAttractionsToMapMarkers = (
  attractions: AttractionProduct[]
): MapMarker[] | [] => {
  const attractionsToProcess = attractions.length > 0 ? attractions : [];

  const processedAttractions = (attractionsToProcess as any[])
    .filter(
      (attraction: any) =>
        attraction.geoLocation?.latitude && attraction.geoLocation?.longitude
    )
    .map((attraction: any) => ({
      ...attraction,
      price: attraction.minPrice,
      currency:
        currencySignsObj[
          attraction.priceCurrency.toLowerCase() as keyof typeof currencySignsObj
        ],
      position: [
        attraction.geoLocation.latitude,
        attraction.geoLocation.longitude,
      ],
      markerType: MARKER_TYPE.ATTRACTION,
    }));
  return processedAttractions;
};

/**
 * Transform events data to include position coordinates
 */
export const transformEventsToMapMarkers = (
  events: any[]
): MapMarker[] | [] => {
  const eventsToProcess = events.length > 0 ? events : [];

  const processedEvents = (eventsToProcess as any[])
    .filter(
      (event: any) =>
        event.venue?.latitude && event.venue?.longitude
    )
    .map((event: any) => ({
      ...event,
      price: event.minTicketPrice?.price,
      currency: event.minTicketPrice?.currency
        ? currencySignsObj[
            event.minTicketPrice.currency.toLowerCase() as keyof typeof currencySignsObj
          ] || event.minTicketPrice.currency
        : '',
      position: [
        event.venue.latitude,
        event.venue.longitude,
      ],
      markerType: MARKER_TYPE.EVENT,
    }));
  return processedEvents;
};

/**
 * Calculate initial bounds to fit all hotels on the map
 */
export const calculateInitialBounds = (
  hotelPins: MapMarker[]
): LatLngBounds | null => {
  if (hotelPins.length === 0) return null;

  const lats = hotelPins.map((hotel) => {
    const [lat] = Array.isArray(hotel.position)
      ? hotel.position
      : [hotel.position[0]];
    return lat;
  });
  const lngs = hotelPins.map((hotel) => {
    const [, lng] = Array.isArray(hotel.position)
      ? hotel.position
      : [0, hotel.position[1]];
    return lng;
  });

  return new LatLngBounds(
    [Math.min(...lats), Math.min(...lngs)],
    [Math.max(...lats), Math.max(...lngs)]
  );
};

/**
 * Calculate the radius in meters for a cluster based on the spread of its hotels
 */
export const calculateClusterRadius = (hotels: MapMarker[]): number => {
  if (hotels.length <= 1) return 500; // Default radius for single hotel clusters

  // Find the maximum distance from cluster center to any hotel
  const centerLat =
    hotels.reduce((sum, h) => {
      const [lat] = Array.isArray(h.position) ? h.position : [h.position[0]];
      return sum + lat;
    }, 0) / hotels.length;

  const centerLng =
    hotels.reduce((sum, h) => {
      const [, lng] = Array.isArray(h.position)
        ? h.position
        : [0, h.position[1]];
      return sum + lng;
    }, 0) / hotels.length;

  const maxDistance = Math.max(
    ...hotels.map((hotel) => {
      const [lat, lng] = Array.isArray(hotel.position)
        ? hotel.position
        : [hotel.position[0], hotel.position[1]];

      // Calculate distance in degrees and convert to meters (rough approximation)
      const degreeDistance = Math.sqrt(
        Math.pow(lat - centerLat, 2) + Math.pow(lng - centerLng, 2)
      );

      // Convert degrees to meters (1 degree ≈ 111,000 meters)
      return degreeDistance * 111000;
    })
  );

  // Add some padding and ensure minimum radius
  return Math.max(maxDistance * 1.2, 500);
};

/**
 * Filter hotels within viewport and create clusters based on zoom level
 */
export const getVisibleItemsWithClustering = (
  hotelPins: MapMarker[],
  mapBounds: MapBounds | null,
  currentZoom: number
): (MapMarker | ClusterGroup)[] => {
  let visibleHotels = hotelPins;

  // Filter by viewport if bounds are available
  if (mapBounds) {
    visibleHotels = hotelPins.filter((hotel) =>
      isInBounds(hotel.position, mapBounds)
    );
  }

  return createClusters(visibleHotels, currentZoom);
};

function toRad(value: number) {
  return (value * Math.PI) / 180;
}

function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  const R = 6371000; // meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function calculateRadiusFromBounds(bounds: MapBounds) {
  const centerLat = (bounds.north + bounds.south) / 2;
  const centerLng = (bounds.east + bounds.west) / 2;

  const corners = [
    [bounds.north, bounds.east],
    [bounds.north, bounds.west],
    [bounds.south, bounds.east],
    [bounds.south, bounds.west],
  ];

  const radius = Math.max(
    ...corners.map(([lat, lng]) =>
      haversineDistance(centerLat, centerLng, lat, lng)
    )
  );

  return Math.round(radius); // in meters
}
